/**
 * ProfileNotifications — SMS adapter for the shared `@tiaki/profile-ui`
 * `ProfileNotificationsSection`.
 *
 * The shared section is presentational: it renders the toggles and raises
 * `onToggle(id, next)`. This wrapper owns the SMS-specific state +
 * persistence — optimistic local state, the `useUpdateUser` mutation, and the
 * success/error toasts (the lib has no toaster), reverting the toggle on
 * failure. Values seed from the user record; the canonical three-item set is
 * the lib's `DEFAULT_NOTIFICATION_ITEMS`.
 *
 * The SMS product's own push categories (ADR-132 Amendment 4) are
 * reunification real-time comms and message/delivery events.
 * Their keys are anchored to the GENERATED OpenAPI schema
 * (`PatchedUserRequest` — the PATCH `/api/v2/user/{id}/` body), not to the
 * hand-rolled `UserFormInitialValues` type, so serializer drift breaks the
 * compile instead of silently desyncing (the SMS hand-rolled-types trap).
 * Backend defaults are opted-IN (absent JSONB sub-key ⇒ true), and the
 * serializer always materializes both fields on reads.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_NOTIFICATION_ITEMS,
  ProfileNotificationsSection,
} from '@tiaki/profile-ui';
import type { components } from '@tiaki/http-client/types';
import { useUpdateUser } from '../../hooks/queries';
import { UserFormInitialValues } from '../../types/UserFormInitialValue';
import { toaster } from '../ui/toaster';

/**
 * The SMS product's push preference items. Their ids are validated against
 * the generated PATCH body schema (`satisfies`) — if the serializer
 * fields ever rename or drop, this fails to compile instead of silently
 * desyncing.
 */
const SMS_PUSH_ITEMS = [
  {
    id: 'toggle_sms_reunification_push_notification',
    label: 'Reunification real-time updates',
    editable: true,
  },
  {
    id: 'toggle_sms_message_delivery_push_notification',
    label: 'Message & delivery events',
    editable: true,
  },
] as const satisfies readonly {
  id: keyof components['schemas']['PatchedUserRequest'];
  label: string;
  editable: boolean;
}[];

type SmsPushToggleKey = (typeof SMS_PUSH_ITEMS)[number]['id'];

type NotificationKey =
  | (typeof DEFAULT_NOTIFICATION_ITEMS)[number]['id']
  | SmsPushToggleKey;

type NotificationSettings = Record<NotificationKey, boolean>;

const ITEMS = [...DEFAULT_NOTIFICATION_ITEMS, ...SMS_PUSH_ITEMS];

const KEYS = ITEMS.map((i) => i.id) as NotificationKey[];

/**
 * The user-record read side for the SMS keys, via the generated read schema
 * (`User` — what `/api/v2/user/` list/detail serializes). The surrounding
 * `userDetails` prop keeps its legacy hand-rolled type; only the new keys
 * read through the generated shape.
 */
type SmsPushToggleReads = Pick<components['schemas']['User'], SmsPushToggleKey>;

type NotificationSectionProps = {
  userDetails?: UserFormInitialValues;
};

export const ProfileNotifications = ({
  userDetails,
}: NotificationSectionProps) => {
  const { t } = useTranslation();
  const updateUserMutation = useUpdateUser();
  const [settings, setSettings] = useState<NotificationSettings>({
    toggle_alert_drills_and_updates_push_notification: true,
    toggle_initial_direct_chat_and_mentions_push_notification: true,
    toggle_all_chats_push_notification: false,
    // Backend defaults: SMS product categories are opted-IN.
    toggle_sms_reunification_push_notification: true,
    toggle_sms_message_delivery_push_notification: true,
  });

  useEffect(() => {
    if (!userDetails) return;
    const record = userDetails as UserFormInitialValues &
      Partial<SmsPushToggleReads>;
    setSettings(
      KEYS.reduce((acc, key) => {
        acc[key] = !!record[key as keyof typeof record];
        return acc;
      }, {} as NotificationSettings),
    );
  }, [userDetails]);

  const handleToggle = async (id: string, next: boolean) => {
    const key = id as NotificationKey;
    setSettings((prev) => ({ ...prev, [key]: next }));
    try {
      await updateUserMutation.mutateAsync({
        id: Number(userDetails?.id),
        data: { [key]: next },
      });
      toaster.create({
        title: t('notification_updated_success', 'Notification settings updated successfully'),
        type: 'success',
        closable: true,
      });
    } catch {
      setSettings((prev) => ({ ...prev, [key]: !next }));
      toaster.create({
        title: t('notification_updated_error', 'Failed to update notification settings'),
        type: 'error',
        closable: true,
      });
    }
  };

  return <ProfileNotificationsSection items={ITEMS} values={settings} onToggle={handleToggle} />;
};

export default ProfileNotifications;
